import { useState, type ReactNode } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useSession } from "../lib/session";
import { useTheme, type ThemeChoice } from "../lib/theme";
import { BrandGlyph, Icon } from "./Icon";

const THEME_OPTIONS: Array<{ value: ThemeChoice; label: string; icon: "sun" | "moon" | "monitor" }> = [
  { value: "light", label: "Light theme", icon: "sun" },
  { value: "dark", label: "Dark theme", icon: "moon" },
  { value: "system", label: "Match the system theme", icon: "monitor" },
];

export function ThemeToggle() {
  const { choice, setChoice } = useTheme();
  return (
    <div className="theme-toggle" role="group" aria-label="Colour theme">
      {THEME_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={choice === option.value}
          aria-label={option.label}
          title={option.label}
          onClick={() => setChoice(option.value)}
        >
          <Icon name={option.icon} size={15} />
        </button>
      ))}
    </div>
  );
}

/**
 * The frame every signed-in screen sits in. `fill` hands the whole viewport below the header to
 * the page, which the lab workspace needs for its three panes.
 */
export function AppShell({ children, fill = false }: { children: ReactNode; fill?: boolean }) {
  const { user, isAdmin, signOut, settings } = useSession();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  const onSignOut = async () => {
    setSigningOut(true);
    await signOut();
    navigate("/sign-in", { replace: true });
  };

  return (
    <div className={fill ? "shell shell-full" : "shell"}>
      <a className="skip-link" href="#main">Skip to the main content</a>
      <header className="masthead">
        <div className="masthead-inner">
          <Link to="/" className="brand">
            <BrandGlyph className="brand-glyph" />
            <span className="brand-name">
              The Terraformed <span>Life</span>
            </span>
          </Link>
          <nav className="nav" aria-label="Main">
            <NavLink to="/" end className={({ isActive }) => (isActive ? "is-current" : "")}>
              Courses
            </NavLink>
            <NavLink to="/account" className={({ isActive }) => (isActive ? "is-current" : "")}>
              Account
            </NavLink>
            <NavLink to="/feedback" className={({ isActive }) => (isActive ? "is-current" : "")}>
              Feedback
            </NavLink>
            {isAdmin ? (
              <NavLink to="/admin" className={({ isActive }) => (isActive ? "is-current" : "")}>
                Admin
              </NavLink>
            ) : null}
          </nav>
          <div className="masthead-end">
            {user ? (
              <span className="whoami">
                <strong>{user.displayName}</strong>
                <span>{user.role === "admin" ? "Administrator" : "Learner"}</span>
              </span>
            ) : null}
            <ThemeToggle />
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => void onSignOut()}
              disabled={signingOut}
            >
              {signingOut ? "Signing out…" : "Sign out"}
            </button>
          </div>
        </div>
      </header>

      <main id="main" className="main" tabIndex={-1}>
        {children}
      </main>

      {fill ? null : (
        <footer className="footline">
          <div className="footline-inner">
            <span>{settings.brand} — graded labs in a real sandbox, a certificate per course.</span>
            <span className="spread">
              Content{" "}
              <a href="https://terraformed.dev/licence" target="_blank" rel="license noopener">CC BY 4.0</a>
              {" · "}
              <a href="https://terraformed.dev/privacy" target="_blank" rel="noopener">Privacy</a>
              {" · "}
              <a href="https://terraformed.dev/terms" target="_blank" rel="noopener">Terms</a>
              {" · "}
              <a href="https://terraformed.dev/covenant" target="_blank" rel="noopener">Covenant</a>
            </span>
          </div>
        </footer>
      )}
    </div>
  );
}
